<template>
  <v-form>
    <v-container :key="reloadPage">
      <v-row>
        <v-col cols="12">
          <h1>Theme options</h1>
          <p>Customize your theme.</p>
          <p class="text-caption">
            Always use 6 characters when setting 'hex' color code. Use only hex
            code (#ffffff) to set colors.
          </p>
          <v-divider></v-divider>
        </v-col>
        <v-col
          :class="{ 'bg-grey-lighten-5': i % 2 === 0, 'bg-white': i % 2 !== 0 }"
          :key="key"
          v-for="(key, i) in getThemeType.themeObjNames"
          cols="12"
          sm="6"
        >
          <h4>{{ `${key[0].toUpperCase()}${key.slice(1)}` }}</h4>
          <div
            style="position: relative"
            :key="prop"
            v-for="prop in Object.keys(getThemeType.getAllItems[key])"
          >
            <v-text-field
              @update:model-value="(val) => updateLocalValue(val, key, prop)"
              @blur="applyThemeChange(key, prop)"
              @keydown.enter="applyThemeChange(key, prop)"
              :label="prop"
              :model-value="getLocalValue(key, prop)"
              variant="filled"
            ></v-text-field>
            <div
              v-if="hexColorCheck(getThemeType.getAllItems[key][prop])"
              class="color-palette"
              :class="{
                'color-palette-active': isColorPaletteActive(key, prop)
              }"
              :style="{ background: getThemeType.getAllItems[key][prop] }"
              @click="changeColor(key, prop, $event)"
            >
              <chrome-picker
                v-if="
                  currentColorKey === key &&
                  currentColorProp === prop &&
                  showColorEditor &&
                  getThemeType.getAllItems[key][prop]
                "
                v-model="colorPickerValue"
                @update:model-value="(val) => updateColor(val, key, prop)"
                :style="colorPickerStyle"
                class="chrome-picker-fixed"
              ></chrome-picker>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { isHexColor } from '@/utils/helpers'
import { Chrome } from '@ckpack/vue-color'
import { theInitialTheme } from '@/utils/setInitialTheme'

export default {
  name: 'Footer',
  components: {
    'chrome-picker': Chrome
  },
  data: () => ({
    showColorEditor: false,
    reloadPage: 0,
    colorEditor: null,
    colorPickerValue: { hex: '#000000' },
    currentColorKey: null,
    currentColorProp: null,
    colorPickerStyle: {},
    localThemeValues: {}
  }),
  watch: {
    reloadCashier() {
      this.reloadPage++
      this.localThemeValues = {}
    }
  },
  methods: {
    ...mapActions(['overwriteTheme']),
    ...mapMutations(['setTheme']),
    changeColor(key, prop, event) {
      this.colorEditor &&
        this.colorEditor.length === 3 &&
        this.setNewTheme(...this.colorEditor, 'update')
      this.currentColorKey = key
      this.currentColorProp = prop
      const hexValue = this.getThemeType.getAllItems[key][prop]
      this.colorPickerValue = { hex: hexValue || '#000000' }
      this.showColorEditor = !this.showColorEditor
      this.colorEditor = null
      if (this.showColorEditor && event && event.target) {
        this.$nextTick(() => {
          const paletteElement = event.target.closest('.color-palette')
          if (paletteElement) {
            this.colorPickerStyle = {
              position: 'absolute',
              top: '40px',
              right: '0px'
            }
          }
        })
      } else {
        this.colorPickerStyle = {}
      }
    },
    updateColor(value, key, prop) {
      this.colorEditor = [value.hex, key, prop]
    },
    updateLocalValue(value, key, prop) {
      if (!this.localThemeValues[key]) {
        this.localThemeValues[key] = {}
      }
      this.localThemeValues[key][prop] = value
    },
    applyThemeChange(key, prop) {
      const localValue = this.getLocalValue(key, prop)
      if (localValue !== undefined) {
        this.setNewTheme(localValue, key, prop)
        // Clear local value after applying
        if (this.localThemeValues[key]) {
          delete this.localThemeValues[key][prop]
          if (Object.keys(this.localThemeValues[key]).length === 0) {
            delete this.localThemeValues[key]
          }
        }
      }
    },
    getLocalValue(key, prop) {
      // Return local value if exists, otherwise return theme value
      if (
        this.localThemeValues[key] &&
        this.localThemeValues[key][prop] !== undefined
      ) {
        return this.localThemeValues[key][prop]
      }
      return this.getThemeType.getAllItems[key][prop]
    },
    setNewTheme(value, key, prop, update) {
      const newTheme = this.fullTheme
      newTheme[key][prop] = value
      const liveTheme = this.getLiveThemePayload(value, key, prop)
      if (liveTheme) {
        // Apply the edit on the running cashier through the live JS API
        // (api.set) instead of tearing the widget down - a full reload
        // re-creates all iframes and causes visible flicker on every edit.
        this.setTheme(newTheme)
        window.CashierInstance.set({ theme: liveTheme })
        this.syncContainerBackground(value, key, prop)
      } else {
        // Not live-updatable - keep the full cashier reload path.
        this.overwriteTheme(newTheme)
      }
    },
    getLiveThemePayload(value, key, prop) {
      if (
        !window.CashierInstanceReady ||
        !window.CashierInstance ||
        typeof window.CashierInstance.set !== 'function'
      ) {
        return null
      }
      if (this.cashierMajorVersion === 'v3') {
        // Cashier V3 deep-merges nested theme objects passed to api.set()
        return { [key]: { [prop]: value } }
      }
      // Cashier V2 only applies flat color values through api.set() - they
      // are wrapped as { color: value } inside the cashier. Anything else
      // (font sizes, radius, logo urls etc) still needs a full reload.
      return prop === 'color' ? { [key]: value } : null
    },
    syncContainerBackground(value, key, prop) {
      // The sandbox paints the container background itself on every full
      // reload (see Cashier.vue) - keep it in sync on live updates too.
      if (key === 'cashierbackground' && prop === 'color') {
        const container = document.querySelector('#cashier-container')
        if (container) {
          container.style.background = value || '#ffffff'
        }
      }
    },
    hexColorCheck(value) {
      return isHexColor(value)
    },
    isColorPaletteActive(key, prop) {
      return (
        this.currentColorKey === key &&
        this.currentColorProp === prop &&
        this.showColorEditor
      )
    }
  },
  computed: {
    ...mapState({
      reloadCashier: ({ config }) => config.reloadCashier,
      theme: ({ theme }) => theme.theme,
      cashierMajorVersion: ({ context }) => context.cashierMajorVersion
    }),
    fullTheme() {
      const initialTheme = theInitialTheme()
      const theme = this.theme
      const newTheme = {
        ...initialTheme
      }
      for (const prop in theme) {
        for (const innerProp in theme[prop]) {
          newTheme[prop][innerProp] = theme[prop][innerProp]
        }
      }
      return newTheme
    },
    getThemeType() {
      const initial = this.fullTheme
      const getAllItems = {}
      const themeObjNames = []
      for (const prop in initial) {
        themeObjNames.push(prop)
        getAllItems[prop] = initial[prop]
      }
      return {
        themeObjNames,
        getAllItems
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-input__slot {
  position: relative;
}
.color-palette {
  position: absolute;
  bottom: 30px;
  right: 0;
  height: 30px;
  width: 40px;
  background: red;
  z-index: 10;
  cursor: pointer;
  border: 1px solid black;
  &.color-palette-active {
    z-index: 10000 !important;
  }
  .chrome-picker-fixed {
    position: absolute !important;
    z-index: 10001 !important;
  }
}
h4 {
  color: #144E38;
}
</style>
